import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext.jsx';
import { getDashboardRoute } from './utils/roleUtils.js';
import Login from './pages/auth/Login.jsx';
import DashboardPage from './pages/shared/Dashboardpage.jsx';
import JobSheetPage from './pages/shared/Jobsheetpage.jsx';
import TaskPage from './pages/shared/Taskpage.jsx';

// Code-split the pages that aren't on the critical first-paint path —
// admins' report tooling shouldn't weigh down a team member's login.
const Register          = lazy(() => import('./pages/auth/Register.jsx'));
const ProfilePage       = lazy(() => import('./pages/shared/Profilepage.jsx'));
const SettingsPage      = lazy(() => import('./pages/shared/Settingspage.jsx'));
const ReportsPage       = lazy(() => import('./pages/admin/Reportspage.jsx'));
const ApprovalsPage     = lazy(() => import('./pages/super-admin/Approvalspage.jsx'));
const DoubtsInboxPage   = lazy(() => import('./pages/admin/Doubtsinboxpage.jsx'));
const ComingSoonPage    = lazy(() => import('./components/ui/Comingsoonpage.jsx'));
const KnowledgeBasePage = lazy(() => import('./pages/public/Knowledgebasepage.jsx'));

/* ------------------------------------------------------------
   Loading screen — shown during initial auth restore
   ------------------------------------------------------------ */
function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas">
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-8 h-8 rounded-full border-2 border-slate-200 animate-spin"
          style={{ borderTopColor: 'var(--accent)' }}
        />
        <p className="text-sm text-muted">Loading ORIN…</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------
   PublicOnly — login/register: redirect to dashboard if signed in
   ------------------------------------------------------------ */
function PublicOnly({ children }) {
  const { session, profile, loading, profileLoading } = useAuth();
  if (loading) return <FullScreenLoader />;
  // Just-signed-in: session exists but profile still being fetched.
  // Show loader so user doesn't see Login form flicker before redirect.
  if (session && profileLoading) return <FullScreenLoader />;
  if (session && profile) {
    return <Navigate to={getDashboardRoute(profile.role)} replace />;
  }
  return children;
}

/* ------------------------------------------------------------
   RoleGuard — allows only users with `allowedRoles`.
   ------------------------------------------------------------ */
function RoleGuard({ allowedRoles, children }) {
  const { session, profile, loading, profileLoading, signOut } = useAuth();
  const location = useLocation();

  // Initial app boot
  if (loading) return <FullScreenLoader />;

  // No session at all -> kick to login
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Session exists, but profile fetch is still in flight (e.g. just after
  // signIn, or after a token refresh). Show loader, not "approval pending",
  // otherwise users see a confusing flash.
  if (profileLoading) return <FullScreenLoader />;

  // Profile fetch finished with no row: the trigger didn't create one,
  // or the row was deleted. Treat as "approval pending" since that's
  // what status='pending' would look like anyway.
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas p-8">
        <div className="card max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full grid place-items-center bg-amber-100 text-amber-700 text-xl">
            ⏳
          </div>
          <h1 className="text-lg font-semibold mb-1">Approval pending</h1>
          <p className="text-sm text-muted mb-5">
            Your registration is with the Super Admin. You&apos;ll receive an
            email once your account is activated.
          </p>
          <button className="btn" onClick={signOut}>Sign out</button>
        </div>
      </div>
    );
  }

  if (profile.status && profile.status !== 'active') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas p-8">
        <div className="card max-w-md w-full p-8 text-center">
          <h1 className="text-lg font-semibold mb-1">Account inactive</h1>
          <p className="text-sm text-muted mb-5">
            Your account is currently {profile.status}. Please contact the Super Admin.
          </p>
          <button className="btn" onClick={signOut}>Sign out</button>
        </div>
      </div>
    );
  }

  if (!allowedRoles.includes(profile.role)) {
    return <Navigate to={getDashboardRoute(profile.role)} replace />;
  }

  return children;
}

/* ============================================================
   App routes
   ============================================================ */
export default function App() {
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <AppRoutes />
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* ----- Public auth ----- */}
      <Route
        path="/login"
        element={<PublicOnly><Login /></PublicOnly>}
      />
      <Route
        path="/register"
        element={<PublicOnly><Register /></PublicOnly>}
      />

      {/* ----- Knowledge base: readable with or without a session ----- */}
      <Route path="/knowledgebase" element={<KnowledgeBasePage />} />

      {/* ----- Super Admin ----- */}
      <Route
        path="/super-admin/*"
        element={
          <RoleGuard allowedRoles={['super']}>
            <Routes>
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="job-sheet" element={<JobSheetPage />} />
              <Route path="task/:taskId" element={<TaskPage />} />
              <Route path="doubts" element={<DoubtsInboxPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route
                path="team"
                element={
                  <ComingSoonPage
                    title="Team"
                    description="Visual org chart and team directory"
                    features={[
                      'Interactive org chart — see who reports to whom across all departments',
                      'Click any person to view their profile and current workload',
                      'Filter by department, role, or task status',
                      'Zoom and pan controls for large teams',
                    ]}
                  />
                }
              />
              <Route path="approvals" element={<ApprovalsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="settings"
                element={<SettingsPage />}
              />
              <Route path="*" element={<Navigate to="/super-admin/dashboard" replace />} />
            </Routes>
          </RoleGuard>
        }
      />

      {/* ----- Admin (SK, GM) ----- */}
      <Route
        path="/admin/*"
        element={
          <RoleGuard allowedRoles={['admin']}>
            <Routes>
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="job-sheet" element={<JobSheetPage />} />
              <Route path="task/:taskId" element={<TaskPage />} />
              <Route path="doubts" element={<DoubtsInboxPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route
                path="team"
                element={
                  <ComingSoonPage
                    title="Team"
                    description="Visual org chart and team directory"
                    features={[
                      'Interactive org chart — see who reports to whom across all departments',
                      'Click any person to view their profile and current workload',
                      'Filter by department, role, or task status',
                      'Zoom and pan controls for large teams',
                    ]}
                  />
                }
              />
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="settings"
                element={<SettingsPage />}
              />
              <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </RoleGuard>
        }
      />

      {/* ----- Team Leader ----- */}
      <Route
        path="/tl/*"
        element={
          <RoleGuard allowedRoles={['tl']}>
            <Routes>
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="job-sheet" element={<JobSheetPage />} />
              <Route path="task/:taskId" element={<TaskPage />} />
              <Route
                path="team"
                element={
                  <ComingSoonPage
                    title="Team"
                    description="Visual org chart and team directory"
                    features={[
                      'Interactive org chart — see who reports to whom across all departments',
                      'Click any person to view their profile and current workload',
                      'Filter by department, role, or task status',
                      'Zoom and pan controls for large teams',
                    ]}
                  />
                }
              />
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="settings"
                element={<SettingsPage />}
              />
              <Route path="*" element={<Navigate to="/tl/dashboard" replace />} />
            </Routes>
          </RoleGuard>
        }
      />

      {/* ----- Team Member ----- */}
      <Route
        path="/member/*"
        element={
          <RoleGuard allowedRoles={['tm']}>
            <Routes>
              <Route index element={<JobSheetPage />} />
              <Route path="job-sheet" element={<JobSheetPage />} />
              <Route path="task/:taskId" element={<TaskPage />} />
              <Route
                path="team"
                element={
                  <ComingSoonPage
                    title="Team"
                    description="Visual org chart and team directory"
                    features={[
                      'Interactive org chart — see who reports to whom across all departments',
                      'Click any person to view their profile and current workload',
                      'Filter by department, role, or task status',
                      'Zoom and pan controls for large teams',
                    ]}
                  />
                }
              />
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="settings"
                element={<SettingsPage />}
              />
              <Route path="*" element={<Navigate to="/member/job-sheet" replace />} />
            </Routes>
          </RoleGuard>
        }
      />

      {/* ----- Root + catch-all ----- */}
      <Route path="/" element={<RoleRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/* ------------------------------------------------------------
   RoleRedirect — root path lands user on their dashboard
   ------------------------------------------------------------ */
function RoleRedirect() {
  const { session, profile, loading, profileLoading, signOut } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!session) return <Navigate to="/login" replace />;
  if (profileLoading) return <FullScreenLoader />;
  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas p-8">
        <div className="card max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full grid place-items-center bg-amber-100 text-amber-700 text-xl">
            ⏳
          </div>
          <h1 className="text-lg font-semibold mb-1">Approval pending</h1>
          <p className="text-sm text-muted mb-5">
            Your registration is with the Super Admin. You&apos;ll receive an
            email once your account is activated.
          </p>
          <button className="btn" onClick={signOut}>Sign out</button>
        </div>
      </div>
    );
  }
  return <Navigate to={getDashboardRoute(profile.role)} replace />;
}